import { useEffect, useState } from 'react'

import { Divider, Grid } from '@mantine/core'

import { useQuery } from '@redwoodjs/web'

import Tag from 'src/components/DataDisplay/Tag/Tag'
import PaginationArea from 'src/components/Navigation/PaginationArea/PaginationArea'
import DateRangeMenu from 'src/components/Overlays/DateRangeMenu'
import { GET_CALLS } from 'src/graphql/activityLogs.graphql'
import { GET_VOICE_AGENT_CALL_METRICS } from 'src/graphql/callAnaltyics.graphql'
import { VoiceAgentAnalysisResultName } from 'src/graphql/types/voiceAgentResult'
import { formatSnakeValue } from 'src/lib/formatters'

import FilterMenu from '../../../../components/Overlays/FiltersMenu/FilterMenu'
import AnalyticsCard from '../../../AnalyticsPage/components/AnalyticsCard'
import { CallAnalysisAlert } from '../../components/CallAnalysisAlert'
import {
  connectedStatusOptions,
  updateUrl,
  voiceAgentAnalysisResultOptions,
} from '../../utils'
import { getUrlParams } from '../../utils'

import VoiceAgentAnalysisTable from './components/VoiceAgentAnalysisTable'
import VoiceAgentBarChart from './components/VoiceAgentBarChart'
import VoiceAgentSwarmPlot from './components/VoiceAgentSwarmPlot'

const VoiceAgentAnalyticsTab = () => {
  const urlParams = getUrlParams()
  const [page, setPage] = useState(1)
  const LIMIT = 15
  const [startDate, setStartDate] = useState(urlParams.defaultStartDate)
  const [endDate, setEndDate] = useState(urlParams.defaultEndDate)
  const [selectedConnectedStatus, setSelectedConnectedStatus] = useState(
    urlParams.defaultConnectedStatus
  )
  const [
    selectedVoiceAgentAnalysisResults,
    setSelectedVoiceAgentAnalysisResults,
  ] = useState(urlParams.defaultVoiceAgentAnalysisResults)

  const { data: callMetricsData, loading: callMetricsLoading } = useQuery(
    GET_VOICE_AGENT_CALL_METRICS,
    {
      variables: {
        filters: {
          dateRange: { gte: startDate, lte: endDate },
          connectedStatuses: selectedConnectedStatus,
        },
      },
    }
  )

  const { data: paginatedData, loading: paginatedLoading } = useQuery(
    GET_CALLS,
    {
      variables: {
        filters: {
          createdAt: { gte: startDate, lte: endDate },
          connectedStatuses: selectedConnectedStatus,
          analysisResults:
            selectedVoiceAgentAnalysisResults.length > 0
              ? selectedVoiceAgentAnalysisResults
              : Object.values(VoiceAgentAnalysisResultName),
        },
        pageInfo: {
          offset: (page - 1) * LIMIT,
          limit: LIMIT,
        },
      },
    }
  )

  const totalConnectedCalls =
    callMetricsData?.getVoiceAgentCallMetrics?.totalConnectedCalls
  const totalCallsWithAnalysis =
    callMetricsData?.getVoiceAgentCallMetrics?.totalCallsWithAnalysis
  const paginatedResults = paginatedData?.getCalls?.items || []

  useEffect(() => {
    setPage(1)
  }, [
    selectedConnectedStatus,
    selectedVoiceAgentAnalysisResults,
    startDate,
    endDate,
  ])

  return (
    <>
      <div className="flex flex-row items-center gap-4">
        <div className="flex flex-1 flex-row gap-2 overflow-auto">
          <Tag showCloseButton={false}>Voice Agent</Tag>
          <Tag showCloseButton={false}>Outbound</Tag>

          {selectedConnectedStatus.map((status) => (
            <Tag
              key={status}
              onClose={() => {
                setSelectedConnectedStatus(
                  selectedConnectedStatus.filter((s) => s !== status)
                )
                updateUrl(
                  'connectedStatus',
                  selectedConnectedStatus.filter((s) => s !== status).join(',')
                )
              }}
            >
              {formatSnakeValue(status)}
            </Tag>
          ))}
          {selectedVoiceAgentAnalysisResults.map((result) => (
            <Tag
              key={result}
              onClose={() => {
                setSelectedVoiceAgentAnalysisResults(
                  selectedVoiceAgentAnalysisResults.filter((s) => s !== result)
                )
                updateUrl(
                  'analysisResults',
                  selectedVoiceAgentAnalysisResults
                    .filter((s) => s !== result)
                    .join(',')
                )
              }}
            >
              {formatSnakeValue(result)}
            </Tag>
          ))}
        </div>
        <Divider orientation="vertical" className="ml-auto" />

        <FilterMenu
          filters={[
            {
              title: 'Analysis Results',
              selected: selectedVoiceAgentAnalysisResults,
              onChange: (value) => {
                setSelectedVoiceAgentAnalysisResults(value)
                updateUrl('analysisResults', value.join(','))
              },
              options: voiceAgentAnalysisResultOptions,
            },
            {
              title: `Voice Agent Status`,
              selected: selectedConnectedStatus,
              onChange: (value) => {
                setSelectedConnectedStatus(value)
                updateUrl('connectedStatus', value.join(','))
              },
              options: connectedStatusOptions,
            },
          ]}
        />
        <DateRangeMenu
          startDate={startDate}
          endDate={endDate}
          handleChange={(value) => {
            setStartDate(value[0])
            setEndDate(value[1])
            updateUrl('dateRange', value.join(','))
          }}
          clearable={false}
        />
      </div>

      <Grid gutter="xs" className="h-full overflow-y-auto overflow-x-hidden">
        <Grid.Col span={12}>
          <CallAnalysisAlert
            totalConnectedCalls={totalConnectedCalls}
            totalCallsWithAnalysis={totalCallsWithAnalysis}
            callMetricsLoading={callMetricsLoading}
          />
        </Grid.Col>
        <Grid.Col span={8}>
          <div className="flex flex-col gap-2">
            <AnalyticsCard
              title="Call Analysis Result Distribution"
              showBadge={false}
              description={`Distribution of call analysis by result category`}
            >
              <VoiceAgentBarChart
                totalConnectedCalls={totalConnectedCalls}
                filters={{
                  dateRange: { gte: startDate, lte: endDate },
                  connectedStatuses: selectedConnectedStatus,
                  analysisResults: selectedVoiceAgentAnalysisResults,
                }}
              />
            </AnalyticsCard>
            <AnalyticsCard
              title="Call Analysis x Call Duration"
              showBadge={false}
              description={`Relationship between call analysis and call duration (max 20 results per category)`}
            >
              <VoiceAgentSwarmPlot
                filters={{
                  dateRange: { gte: startDate, lte: endDate },
                  connectedStatuses: selectedConnectedStatus,
                  analysisResults: selectedVoiceAgentAnalysisResults,
                }}
              />
            </AnalyticsCard>
          </div>
        </Grid.Col>
        <Grid.Col span={4}>
          <AnalyticsCard
            title="Call Analysis Result List"
            showBadge={false}
            loading={paginatedLoading}
            description={'List of all call analysis results'}
            headerChildren={
              <div className="flex flex-col items-center justify-center">
                <div className="flex flex-row items-baseline gap-2 text-xl text-doubleNickel-gray-800">
                  {paginatedData?.getCalls?.totalCount}
                </div>
                <div className="flex flex-row items-center gap-2 text-xs font-medium text-doubleNickel-gray-600">
                  calls
                </div>
              </div>
            }
          >
            <VoiceAgentAnalysisTable
              voiceAgentAnalysisResults={paginatedResults}
            />
            <PaginationArea
              page={page}
              size="xs"
              totalPages={Math.ceil(
                paginatedData?.getCalls?.totalCount / LIMIT
              )}
              onPageChange={(page) => {
                setPage(page)
              }}
            />
          </AnalyticsCard>
        </Grid.Col>
      </Grid>
    </>
  )
}

export default VoiceAgentAnalyticsTab
